import {
  Chip,
  createMuiTheme,
  Icon,
  IconButton,
  MuiThemeProvider,
  Tooltip,
  Typography,
  WithStyles,
  withStyles,
} from '@material-ui/core';
import { Check, Close } from '@material-ui/icons';
import * as React from 'react';
import { compose } from 'recompose';
import styles from '@/app/components/Worklist/worklists/styles';
import {
  CollaborationTemplateAutomationTriggerCondition,
  ICollaborationTemplate,
  OptionType
} from '@/app/components/Worklist/types';
import translate from '@/app/utils/translate';
import { red, blue } from '@material-ui/core/colors';
import SortIcon from '@material-ui/icons/Sort';
import { Enum, getCompanyLanguages } from '@/old/utils/helper';

const InactiveChip = (props: any) => {
  const inactiveTheme = createMuiTheme({
    palette: {
      primary: {
        main: red[600],
      },
    },
    typography: { useNextVariants: true },
  });

  return (
    <MuiThemeProvider theme={inactiveTheme}>
      <Chip {...props} />
    </MuiThemeProvider>
  );
};

const ActiveChip = (props: any) => {
  const inactiveTheme = createMuiTheme({
    palette: {
      primary: {
        main: blue[600],
      },
    },
    typography: { useNextVariants: true },
  });

  return (
      <MuiThemeProvider theme={inactiveTheme}>
        <Chip {...props} />
      </MuiThemeProvider>
  );
};

type InnerProps = WithStyles<typeof styles>;
type OwnProps = {
  collaborationTemplate: ICollaborationTemplate;
  isExpanded: boolean;
  setAddTaskCollaborationTemplateId: (id: number | undefined) => void;
  setOpenSortDialog: (open: boolean) => void;
  menuComponent?: React.ReactNode;
  taskTypes: Enum[];
};
type Props = OwnProps & InnerProps;

const enhance = compose<Props, OwnProps>(
  withStyles(styles, {
    withTheme: true,
  }),
);

export const WorklistPanelSummary = enhance((props: Props) => {
  const {
    classes,
    collaborationTemplate,
    isExpanded,
    setAddTaskCollaborationTemplateId,
    setOpenSortDialog,
    menuComponent,
    taskTypes,
  } = props;

  const handleAddTaskOpen = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setAddTaskCollaborationTemplateId(collaborationTemplate.id);
  };

  const handleSortDialogOpen = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setOpenSortDialog(true);
  };

  const resolveType = (type: string): string => {
    const foundType = taskTypes.find((t) => t.code === type);

    return !!foundType ? foundType.name : type;
  };

  const resolveLanguage = (language: string): string => {
    const foundLanguage = getCompanyLanguages().find((t) => t.fComLanguageCode === language);

    return !!foundLanguage ? foundLanguage.fComLanguageName : language;
  };

  const resolveTriggerCondition = (condition?: CollaborationTemplateAutomationTriggerCondition) => {
    const triggerConditionOptions = [
      {
        value: CollaborationTemplateAutomationTriggerCondition.EMPLOYMENT_START_DATE,
        label: translate.t('fEmploymentStartDateInput'),
      },
      {
        value: CollaborationTemplateAutomationTriggerCondition.EMPLOYMENT_END_DATE,
        label: translate.t('fEmploymentEndDateInput'),
      },
    ];

    const foundOption = triggerConditionOptions.find((o) => o.value === condition);

    return !!foundOption ? foundOption.label : '';
  };

  const resolveTriggerOffset = (offset?: number) => {
    if (typeof offset !== 'undefined') {
      if (offset < 0) {
        return ` - ${offset * -1} ${translate.t('laDaysBefore')}`;
      } else if (offset > 0) {
        return ` - ${offset} ${translate.t('laDaysAfter')}`;
      }
    }

    return '';
  };

  const resolveFirstUnitAndCount = (units?: OptionType[]) => {
    if (!!units && units.length > 0) {
      return units.length > 1 ? translate.t('label_units', {count: units.length}) : units[0].label;
    }

    return '';
  };

  const resolveUnits = (units?: OptionType[]) => {
    if (!!units && units.length > 0) {
      return `${translate.t('label_units', {count: units.length})}: ${units.map((u) => u.label).join(', ')}`;
    }

    return '';
  };

  return (
    <>
      <div className={classes.expansionPanelSummaryContent}>
        <Typography
          component="span"
          className={classes.collaborationTemplateHeading}
        >
          {collaborationTemplate.title}
        </Typography>

        {menuComponent}

        <Typography component="span" className={classes.typeLabel}>
          {`${translate.t('table_col_type')}: ${resolveType(collaborationTemplate.type)}`}
        </Typography>
        <Typography component="span" className={classes.typeLabel}>
          {`${translate.t('laDefaultTemplateLanguage')}: ${resolveLanguage(collaborationTemplate.language)}`}
        </Typography>
        {collaborationTemplate.isUsed ? (<ActiveChip
            className={classes.inactiveChip}
            label={translate.t('laInUse')}
            color="primary"
        />) : (<InactiveChip
            className={classes.inactiveChip}
            label={translate.t('laNotInUse')}
            color="primary"
        />)}
        {collaborationTemplate.active ? (<ActiveChip
            className={classes.inactiveChip}
            label={translate.t('laActive')}
            color="primary"
        />) : (
            <InactiveChip
                className={classes.inactiveChip}
                label={translate.t('label_inactive')}
                color="primary"
            />
        )}
      </div>
      {collaborationTemplate.automationEnabled ? (
          <div>
            <div>{translate.t('laAutomationEnabled')} <Check style={{color: 'green', verticalAlign: 'center'}} fontSize={'inherit'} /></div>
            {collaborationTemplate.automationUnits.length && (
              <div title={resolveUnits(collaborationTemplate.automationUnits)}>
                {`${translate.t('laOrgUnits')}: ${resolveFirstUnitAndCount(collaborationTemplate.automationUnits)}`}
              </div>
            )}

            <div>{
              collaborationTemplate.automationTriggerCondition ?
                  `${translate.t('laTriggerCondition')}: ${
                resolveTriggerCondition(collaborationTemplate.automationTriggerCondition)
              }${resolveTriggerOffset(collaborationTemplate.automationTriggerOffset || 0)}` : ''
            }</div>
          </div>
      ) : (
          <div>
            <div>{translate.t('laAutomationEnabled')} <Close style={{ verticalAlign: 'center' }} color={'error'} fontSize={'inherit'} /></div>
          </div>
      )}
      <div className={classes.expansionPanelSummaryContent}>
        {isExpanded && (
          <>
            <Tooltip title={translate.t('laSort')}>
              <IconButton
                color="primary"
                component="span"
                onClick={handleSortDialogOpen}
                disabled={collaborationTemplate.isUsed}
              >
                <SortIcon />
              </IconButton>
            </Tooltip>
            <IconButton color="primary" onClick={handleAddTaskOpen} disabled={collaborationTemplate.isUsed}>
              <Icon>add_circle_outlined</Icon>
            </IconButton>
          </>
        )}
      </div>
    </>
  );
});
